import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalDataTrack, Room, TwilioError } from 'twilio-video';
import useHandleOnDisconnect from '../../components/VideoProvider/useHandleOnDisconnect/useHandleOnDisconnect';
import BackendService from '../../services/BackendService';
import useCurrentSessionState from '../useCurrentSessionState/useCurrentSessionState';
import usePractitionerControlsContext from '../usePractitionerControlsContext/usePractitionerControlsContext';
import useVideoContext from '../useVideoContext/useVideoContext';
import useAppContext from '../useAppContext/useAppContext';
import { CLIENT_ROOM_NOT_FOUND_RETRY_WINDOW_MS } from '../../utils/consts';
import { captureTwilioDuplicateIdentityDiagnostic } from '../../logging/twilioDiagnostics';

const ACCESS_TOKEN_EXPIRED_ERROR_CODE = 20104;
const ROOM_NOT_FOUND_ERROR_CODE = 53106;
const DUPLICATE_IDENTITY_ERROR_CODE = 53205;
const CONNECT_RETRY_DELAY_MS = 1500;
const DUPLICATE_IDENTITY_BACKOFF_MS = 5000;

const useClientCommands = () => {
  const { t } = useTranslation();
  const [isConnectedToCall, setIsConnectedToCall] = useState(false);
  const [connectedToken, setConnectedToken] = useState('');
  const [twilioToken, setTwilioToken] = useState<string>('');
  const { setProjectionType } = usePractitionerControlsContext();
  const { room, connect, localTracks } = useVideoContext();
  const { sessionId, treatmentId } = useAppContext();
  const localDataTrack = localTracks.find((track) => track.kind === 'data') as LocalDataTrack;
  const [retryTick, setRetryTick] = useState(0);
  const connectInFlightRef = useRef(false);
  const connectRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alertedFailureTokenRef = useRef<string | null>(null);
  const expiredTokenPausedRef = useRef<string | null>(null);
  const roomNotFoundFirstSeenAtRef = useRef<number | null>(null);
  const roomNotFoundPausedTokenRef = useRef<string | null>(null);
  const duplicateIdentityPausedRef = useRef<string | null>(null);
  const duplicateIdentityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noParticipantTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noParticipantRoomRef = useRef<any>(null);
  const noParticipantConnectedHandlerRef = useRef<(() => void) | null>(null);
  useHandleOnDisconnect(room, () => setIsConnectedToCall(false));
  const { token, generatedUUID, mediaProjection } = useCurrentSessionState(1000);

  const clearConnectRetryTimeout = useCallback(() => {
    if (connectRetryTimeoutRef.current !== null) {
      clearTimeout(connectRetryTimeoutRef.current);
      connectRetryTimeoutRef.current = null;
    }
  }, []);

  const clearDuplicateIdentityTimeout = useCallback(() => {
    if (duplicateIdentityTimeoutRef.current !== null) {
      clearTimeout(duplicateIdentityTimeoutRef.current);
      duplicateIdentityTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (token) {
      setTwilioToken((previousToken) => {
        if (previousToken !== token) {
          expiredTokenPausedRef.current = null;
          roomNotFoundFirstSeenAtRef.current = null;
          roomNotFoundPausedTokenRef.current = null;
          duplicateIdentityPausedRef.current = null;
          clearConnectRetryTimeout();
          clearDuplicateIdentityTimeout();
        }
        return token;
      });
    }
  }, [token, clearConnectRetryTimeout, clearDuplicateIdentityTimeout]);

  useEffect(() => {
    if (generatedUUID) {
      BackendService.patchReceivedProperty(generatedUUID);
    }
  }, [generatedUUID]);

  useEffect(() => {
    setProjectionType(mediaProjection);
  }, [mediaProjection, setProjectionType]);

  const clearNoParticipantGuard = useCallback(() => {
    if (noParticipantTimeoutRef.current !== null) {
      clearTimeout(noParticipantTimeoutRef.current);
      noParticipantTimeoutRef.current = null;
    }
    if (noParticipantRoomRef.current && noParticipantConnectedHandlerRef.current) {
      noParticipantRoomRef.current.off('participantConnected', noParticipantConnectedHandlerRef.current);
    }
    noParticipantRoomRef.current = null;
    noParticipantConnectedHandlerRef.current = null;
  }, []);

  const ConnectToRoom = useCallback(
    async (tokenValue: string) => {
      if (!tokenValue || !localDataTrack) {
        return;
      }

      // The client page connects to the room by token.
      return await connect(tokenValue, localDataTrack, {
        suppressErrorCodes: [ACCESS_TOKEN_EXPIRED_ERROR_CODE, DUPLICATE_IDENTITY_ERROR_CODE],
      });
    },
    [connect, localDataTrack]
  );

  useEffect(() => {
    if (
      !twilioToken ||
      !localDataTrack ||
      connectedToken === twilioToken ||
      connectInFlightRef.current ||
      expiredTokenPausedRef.current === twilioToken ||
      roomNotFoundPausedTokenRef.current === twilioToken ||
      duplicateIdentityPausedRef.current === twilioToken
    ) {
      return;
    }

    connectInFlightRef.current = true;

    const runConnect = async () => {
      try {
        const connectedRoom = await ConnectToRoom(twilioToken);
        if (!connectedRoom) {
          return;
        }

        clearNoParticipantGuard();

        const hasRemoteParticipants =
          typeof connectedRoom.participants?.size === 'number' && connectedRoom.participants.size > 0;

        setConnectedToken(twilioToken);
        setIsConnectedToCall(hasRemoteParticipants);
        alertedFailureTokenRef.current = null;
        expiredTokenPausedRef.current = null;
        roomNotFoundFirstSeenAtRef.current = null;
        roomNotFoundPausedTokenRef.current = null;
        duplicateIdentityPausedRef.current = null;
        clearConnectRetryTimeout();
        clearDuplicateIdentityTimeout();
        if (!hasRemoteParticipants) {
          // If no practitioner joins shortly after connect, treat this as a stale call token and disconnect.
          const handleParticipantConnected = () => {
            clearNoParticipantGuard();
            setIsConnectedToCall(true);
          };
          noParticipantRoomRef.current = connectedRoom;
          noParticipantConnectedHandlerRef.current = handleParticipantConnected;
          connectedRoom.on('participantConnected', handleParticipantConnected);

          noParticipantTimeoutRef.current = setTimeout(() => {
            const stillHasNoRemoteParticipants =
              typeof connectedRoom.participants?.size !== 'number' || connectedRoom.participants.size === 0;
            clearNoParticipantGuard();
            if (stillHasNoRemoteParticipants && connectedRoom.state !== 'disconnected') {
              connectedRoom.disconnect();
              setIsConnectedToCall(false);
            }
          }, 3000);
        }

        try {
          BackendService.sendMediaStatusReport(sessionId, treatmentId, 'call://connected', 1, 1.0);
        } catch {}
      } catch (e) {
        const errorCode = (e as { code?: number })?.code;
        const isExpiredToken = errorCode === ACCESS_TOKEN_EXPIRED_ERROR_CODE;
        const isRoomNotFound = errorCode === ROOM_NOT_FOUND_ERROR_CODE;
        const isDuplicateIdentity = errorCode === DUPLICATE_IDENTITY_ERROR_CODE;

        // A stale token (previous call's room is gone, or the token expired) is expected
        // on page load and is already reported as a tagged Sentry warning by useRoom.
        // Log it as a warning so captureConsole doesn't add a duplicate untagged error.
        if (isExpiredToken || isRoomNotFound) {
          console.warn('[Client connect] stale call token, waiting for a new one', e);
        } else {
          console.error(e);
        }

        if (isExpiredToken) {
          expiredTokenPausedRef.current = twilioToken;
          roomNotFoundFirstSeenAtRef.current = null;
          roomNotFoundPausedTokenRef.current = null;
        } else if (isRoomNotFound) {
          const now = Date.now();
          if (roomNotFoundFirstSeenAtRef.current === null) {
            roomNotFoundFirstSeenAtRef.current = now;
          }

          const roomNotFoundElapsedMs = now - roomNotFoundFirstSeenAtRef.current;
          if (roomNotFoundElapsedMs >= CLIENT_ROOM_NOT_FOUND_RETRY_WINDOW_MS) {
            roomNotFoundPausedTokenRef.current = twilioToken;
            clearConnectRetryTimeout();
            return;
          }
        } else if (isDuplicateIdentity) {
          // Another participant with identity=client is still in the room.
          // Pause this token and let the backoff timer retry so Twilio has
          // time to release the previous participant server-side.
          captureTwilioDuplicateIdentityDiagnostic(e, 'connect_failed');
          duplicateIdentityPausedRef.current = twilioToken;
          roomNotFoundFirstSeenAtRef.current = null;
          roomNotFoundPausedTokenRef.current = null;
          clearConnectRetryTimeout();
          clearDuplicateIdentityTimeout();
          const pausedToken = twilioToken;
          duplicateIdentityTimeoutRef.current = setTimeout(() => {
            duplicateIdentityTimeoutRef.current = null;
            if (duplicateIdentityPausedRef.current === pausedToken) {
              duplicateIdentityPausedRef.current = null;
              setRetryTick((value) => value + 1);
            }
          }, DUPLICATE_IDENTITY_BACKOFF_MS);
          return;
        } else {
          roomNotFoundFirstSeenAtRef.current = null;
          roomNotFoundPausedTokenRef.current = null;
        }

        if (
          !isExpiredToken &&
          !isRoomNotFound &&
          !isDuplicateIdentity &&
          alertedFailureTokenRef.current !== twilioToken
        ) {
          alertedFailureTokenRef.current = twilioToken;
          alert(t('Could not connect to room'));
        }

        clearConnectRetryTimeout();
        if (isExpiredToken) {
          return;
        }
        connectRetryTimeoutRef.current = setTimeout(() => {
          connectRetryTimeoutRef.current = null;
          setRetryTick((value) => value + 1);
        }, CONNECT_RETRY_DELAY_MS);
      } finally {
        connectInFlightRef.current = false;
      }
    };

    void runConnect();
  }, [
    ConnectToRoom,
    twilioToken,
    connectedToken,
    localDataTrack,
    sessionId,
    treatmentId,
    t,
    retryTick,
    clearNoParticipantGuard,
    clearConnectRetryTimeout,
    clearDuplicateIdentityTimeout,
  ]);

  useEffect(() => {
    if (!room || typeof room.on !== 'function' || typeof room.off !== 'function') {
      return;
    }

    const handleRoomDisconnected = (_disconnectedRoom: Room, error?: TwilioError) => {
      if (!error || error.code !== DUPLICATE_IDENTITY_ERROR_CODE) {
        return;
      }

      // Clear connectedToken so the reconnect effect can pick the same token
      // up again once the backoff pause expires.
      setConnectedToken('');
      setIsConnectedToCall(false);
      alertedFailureTokenRef.current = null;

      const pausedToken = twilioToken;
      if (pausedToken) {
        duplicateIdentityPausedRef.current = pausedToken;
        clearConnectRetryTimeout();
        clearDuplicateIdentityTimeout();
        duplicateIdentityTimeoutRef.current = setTimeout(() => {
          duplicateIdentityTimeoutRef.current = null;
          if (duplicateIdentityPausedRef.current === pausedToken) {
            duplicateIdentityPausedRef.current = null;
            setRetryTick((value) => value + 1);
          }
        }, DUPLICATE_IDENTITY_BACKOFF_MS);
      }
    };

    room.on('disconnected', handleRoomDisconnected);
    return () => {
      room.off('disconnected', handleRoomDisconnected);
    };
  }, [room, twilioToken, clearConnectRetryTimeout, clearDuplicateIdentityTimeout]);

  useEffect(() => {
    return () => {
      clearNoParticipantGuard();
      clearConnectRetryTimeout();
      clearDuplicateIdentityTimeout();
    };
  }, [clearNoParticipantGuard, clearConnectRetryTimeout, clearDuplicateIdentityTimeout]);

  return {
    twilioToken,
    localDataTrack,
    room,
    connect,
    isConnectedToCall,
  };
};

export default useClientCommands;
